import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import * as braze from '@braze/web-sdk';
import type { UserInfo } from '@intigral/domain/lib/core/entities/UserInfo';
import {
    flushBrazeData,
    hasValidUserData,
    initializeBraze,
    isBrazeInitialized,
    logBrazeCustomEvent,
    resetBrazeIdentity,
    subscribeToBrazeInit,
} from '../services/braze/brazeService';

export interface IBrazeContentCard {
    id: string;
    viewed: boolean;
    title: string;
    imageUrl: string;
    description: string;
    created: string;
    updated: string;
    categories: Array<any>;
    expiresAt: string;
    url: any;
    linkText: string;
    aspectRatio: number;
    extras: {
        id: string;
        type: string;
    };
    pinned: boolean;
    dismissible: boolean;
    clicked: boolean;
    dismissed: boolean;
    isControl: boolean;
    test: boolean;
    _: string;
    S: boolean;
}

/**
 * Initializes Braze once CMS config and authenticated user data are available.
 * Runs during render so the SDK is ready before child effects (e.g. content cards) subscribe.
 * Re-identifies automatically when uid replaces accountId on the user object.
 */
export function useBrazeInit(userInfo: UserInfo | null | undefined, configReady: boolean): void {
    if (configReady && hasValidUserData(userInfo)) {
        initializeBraze(userInfo);
    }
}

export function useBrazeCards() {
    const [cards, setCards] = useState<braze.Card[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const isReady = useSyncExternalStore(subscribeToBrazeInit, isBrazeInitialized);

    const applyFeed = useCallback((feed?: braze.ContentCards | null) => {
        if (!feed) {
            return;
        }
        // keep control cards: Braze needs their impressions
        setCards(feed.cards.filter((card) => !(card as unknown as IBrazeContentCard).clicked));
        setUnreadCount(feed.getUnviewedCardCount());
    }, []);

    useEffect(() => {
        if (!isReady) {
            return;
        }

        const subscription = braze.subscribeToContentCardsUpdates(applyFeed);
        braze.requestContentCardsRefresh();
        braze.automaticallyShowInAppMessages();
        // Braze does not replay the cache to subscribers
        applyFeed(braze.getCachedContentCards());

        return () => {
            if (subscription) {
                braze.removeSubscription(subscription);
            }
        };
    }, [applyFeed, isReady]);

    /** Braze does not notify subscribers on impressions */
    const markRead = useCallback(() => setUnreadCount(0), []);

    return { cards: cards as unknown as Partial<IBrazeContentCard>[], unreadCount, markRead };
}

const useBraze = () => ({
    logBrazeCustomEvent,
    flushBrazeData: async () => {
        await flushBrazeData();
        resetBrazeIdentity();
    },
});

export default useBraze;
